import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { InputNumber } from 'antd';
import { metricsDetails } from '@/constants';
import { StageTaskType } from '@/store/Lifecycle/constants';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { getDecimalPrecision } from '@/utils/util-measurements';
import Super from '@/comp/Super';
import { AutoSaverFormItem } from './AutoSaverFormItem';
import { useTask } from './Provider';
import { getTaskFieldMetadata } from './constants';
import { computeValidateStatus } from './utils';

export const MeasurementUnitField = observer(() => {
  const { userStore } = useStores();
  const { tvalue, tunit } = useMeasurement();
  const { t } = useTranslation();
  const task = useTask<StageTaskType.MeasurementUnit>();
  const { name, value, validateStatus, help, isReadOnly, isSaving, options, required } = getTaskFieldMetadata(task);
  const measurementSystem = userStore.user.measurementSystem.toUpperCase();
  const unit = metricsDetails[options[0]?.value]?.unit;
  const precision = getDecimalPrecision(unit, measurementSystem);
  const step = typeof precision === 'number' ? 1 / Math.pow(10, precision) : 1;

  const convertToMetric = useCallback(
    (value: number) => tvalue(value, tunit(unit), 'METRIC', measurementSystem),
    [measurementSystem],
  );

  const convertFromMetric = useCallback((value: number) => tvalue(value, unit, measurementSystem), [measurementSystem]);

  const computedValidateStatus = computeValidateStatus(value, validateStatus);

  return (
    <AutoSaverFormItem
      name={name}
      initialValue={value}
      className="stage-task-input-field"
      hasFeedback
      validateStatus={computedValidateStatus}
      help={t(help)}
      required={required}
    >
      <InputNumber
        className="stage-task-input"
        size="large"
        value={value}
        step={convertToMetric(step)}
        disabled={isReadOnly || isSaving}
        formatter={value => `${convertFromMetric(value)}`}
        parser={value => convertToMetric(+value)}
        addonAfter={
          <Trans i18nKey={tunit(unit).replace('^', '')}>
            <Super />
          </Trans>
        }
      />
    </AutoSaverFormItem>
  );
});
